import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Alert, Box, Button, Card, Chip, Radio, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Iconify } from '../../../../components';
import {
    DENTALLY_STATUS,
    buildIdentityResolution,
    collectLeadIds,
    dentallyResolutionFor,
} from '../../../../utils/leadIdentityApi';
import { archiveReasonLabel } from './createLeadSearchHelpers';

const humanize = (val) => (val || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const MAX_CRM_CARDS = 3;

const sectionCard = { p: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' };

const matchLabel = (matchOn = []) => (matchOn.length ? `Matched on ${matchOn.join(' + ')}` : '');

function LeadSummaryLine({ lead }) {
    return (
        <>
            <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                {lead.name || `Lead #${lead.id}`}
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                {[lead.phone_masked, lead.email_masked].filter(Boolean).join(' · ')}
            </Typography>
        </>
    );
}
LeadSummaryLine.propTypes = { lead: PropTypes.object.isRequired };

/**
 * "Before you continue" — shown when the identity check found an existing CRM lead
 * (active or archived) and/or a Dentally patient. The operator picks exactly one way forward.
 *
 * Wizard use passes onFollowUp / onOpenLead / onRestore; compact surfaces (call script,
 * composers) omit onFollowUp and get the dialog-sized variant.
 */
export default function IdentityCheckStep({
    check, onCreateNew, onOpenLead, onFollowUp, onRestore, compact,
}) {
    const active = check?.crm?.active || [];
    const archived = check?.crm?.archived || [];
    const crmCards = useMemo(
        () => [
            ...active.map((lead) => ({ lead, kind: 'active' })),
            ...archived.map((lead) => ({ lead, kind: 'archived' })),
        ],
        [active, archived],
    );
    const shownCards = crmCards.slice(0, MAX_CRM_CARDS);
    const hiddenCount = crmCards.length - shownCards.length;
    const hasCrm = crmCards.length > 0;

    const dStatus = check?.dentally?.status;
    const candidates = (check?.dentally?.candidates || []).slice(0, 3);
    const isMatched = dStatus === DENTALLY_STATUS.MATCHED && candidates.length > 0;
    const isAsk = (dStatus === DENTALLY_STATUS.ASK || dStatus === DENTALLY_STATUS.AMBIGUOUS) && candidates.length > 0;
    const isUnavailable = dStatus === DENTALLY_STATUS.UNAVAILABLE;

    // '' = declined / none chosen yet; MATCHED preselects its patient.
    const [picked, setPicked] = useState(isMatched ? String(candidates[0].dentally_patient_id) : undefined);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState('');

    const askUnanswered = isAsk && picked === undefined;
    const selectedId = picked || '';

    const submit = () => {
        const { dentallyPatientId, dentallyCheck } = dentallyResolutionFor(check, selectedId);
        onCreateNew(buildIdentityResolution({
            decision: 'create_new',
            acknowledgedLeadIds: collectLeadIds(check),
            dentallyPatientId,
            dentallyCheck,
        }), { dentallyPatientId });
    };

    const runRestore = async (lead) => {
        setError('');
        setBusyId(lead.id);
        try {
            const res = await onRestore(lead);
            if (res && res.ok === false) setError(res.message || 'Could not restore this lead. Please try again.');
        } catch (e) {
            setError('Could not restore this lead. Please try again.');
        } finally {
            setBusyId(null);
        }
    };

    const renderCrmCard = ({ lead, kind }) => {
        const isArchived = kind === 'archived';
        const reason = archiveReasonLabel(lead.archive_reason);
        const archivedAt = formatDate(lead.archived_at);
        return (
            <Card key={`${kind}-${lead.id}`} sx={sectionCard} data-testid={`identity-card-${kind}`}>
                <Stack direction='row' spacing={1} alignItems='flex-start'>
                    <Iconify
                        icon={isArchived ? 'mdi:archive-outline' : 'mdi:account-check-outline'}
                        width={20}
                        sx={{ color: isArchived ? 'warning.main' : 'primary.main', mt: 0.25, flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction='row' spacing={1} alignItems='center' sx={{ flexWrap: 'wrap' }}>
                            <LeadSummaryLine lead={lead} />
                            <Chip
                                size='small'
                                variant='soft'
                                color={isArchived ? 'warning' : 'success'}
                                label={isArchived ? 'Archived' : 'Active'}
                            />
                        </Stack>
                        <Stack direction='row' spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                            {!isArchived && lead.status && (
                                <Chip size='small' variant='soft' label={humanize(lead.status)} />
                            )}
                            {lead.treatment && (
                                <Chip size='small' variant='soft' icon={<Iconify icon='mdi:tooth-outline' width={14} height={14} />} label={lead.treatment} />
                            )}
                            {isArchived && archivedAt && (
                                <Chip size='small' variant='soft' icon={<Iconify icon='mdi:calendar-outline' width={14} height={14} />} label={`Archived ${archivedAt}`} />
                            )}
                            {isArchived && reason && (
                                <Chip size='small' variant='soft' color='warning' label={`Reason: ${reason}`} />
                            )}
                            {lead.source_type && (
                                <Chip
                                    size='small'
                                    variant='soft'
                                    color='info'
                                    icon={<Iconify icon='mdi:web' width={14} height={14} />}
                                    label={`Original source: ${humanize(lead.source_type)}`}
                                />
                            )}
                            {matchLabel(lead.match_on) && (
                                <Chip size='small' variant='outlined' label={matchLabel(lead.match_on)} />
                            )}
                        </Stack>
                        {isArchived && lead.has_ad_click && (
                            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.75 }}>
                                Restoring keeps the original ad click, so the conversion is attributed correctly.
                            </Typography>
                        )}
                        <Stack direction='row' spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                            {isArchived ? (
                                <LoadingButton
                                    size='small'
                                    variant='contained'
                                    color='warning'
                                    loading={busyId === lead.id}
                                    disabled={busyId != null || !onRestore}
                                    onClick={() => runRestore(lead)}
                                >
                                    {compact ? 'Restore lead' : 'Restore & continue'}
                                </LoadingButton>
                            ) : (
                                <>
                                    {onFollowUp && (
                                        <Button size='small' variant='contained' disabled={busyId != null} onClick={() => onFollowUp(lead)}>
                                            Follow up instead
                                        </Button>
                                    )}
                                    <Button size='small' variant='outlined' disabled={busyId != null} onClick={() => onOpenLead?.(lead)}>
                                        Open lead
                                    </Button>
                                </>
                            )}
                            {isArchived && (
                                <Button size='small' variant='outlined' color='inherit' disabled={busyId != null} onClick={() => onOpenLead?.(lead)}>
                                    Open lead
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </Card>
        );
    };

    const candidateRow = (c, selectable) => {
        const id = String(c.dentally_patient_id);
        // Single-signal candidates arrive minimal: "Jane S." + birth year only.
        const detail = [
            c.dob ? `DOB ${c.dob}` : '',
            !c.dob && c.birth_year ? `born ${c.birth_year}` : '',
            c.phone_masked,
            c.email_masked,
        ].filter(Boolean).join(' · ');
        return (
            <Stack
                key={id}
                direction='row'
                alignItems='center'
                spacing={1}
                data-testid={`identity-candidate-${id}`}
                sx={{ py: 0.5 }}
            >
                {selectable && (
                    <Radio
                        size='small'
                        checked={picked === id}
                        onChange={() => setPicked(id)}
                        inputProps={{ 'aria-label': `Select Dentally patient ${c.name || id}` }}
                    />
                )}
                <Box>
                    <Typography variant='subtitle2'>
                        {c.name || 'Dentally patient'} <Box component='span' sx={{ color: 'text.secondary', fontWeight: 400 }}>#{id}</Box>
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                        {detail}
                        {c.signals?.length ? `${detail ? ' · ' : ''}matched on ${c.signals.join(' + ')}` : ''}
                    </Typography>
                </Box>
            </Stack>
        );
    };

    const renderDentally = () => {
        if (isMatched) {
            const c = candidates[0];
            const linked = Boolean(selectedId);
            return (
                <Card sx={sectionCard} data-testid='identity-card-dentally'>
                    <Stack direction='row' spacing={1} alignItems='flex-start'>
                        <Iconify icon='mdi:tooth-outline' width={20} sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                                {linked ? '✓ Dentally patient found — will be linked' : 'Dentally patient will not be linked'}
                            </Typography>
                            {candidateRow(c, false)}
                            <Button
                                size='small'
                                color='inherit'
                                sx={{ mt: 0.5 }}
                                onClick={() => setPicked(linked ? '' : String(c.dentally_patient_id))}
                            >
                                {linked ? 'Not this patient' : 'Link this patient'}
                            </Button>
                        </Box>
                    </Stack>
                </Card>
            );
        }
        if (isAsk) {
            return (
                <Card sx={sectionCard} data-testid='identity-card-dentally'>
                    <Stack direction='row' spacing={1} alignItems='flex-start'>
                        <Iconify icon='mdi:tooth-outline' width={20} sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                                Is this one of the patients in Dentally?
                            </Typography>
                            {candidates.map((c) => candidateRow(c, true))}
                            <Stack direction='row' alignItems='center' spacing={1} sx={{ py: 0.5 }}>
                                <Radio
                                    size='small'
                                    checked={picked === ''}
                                    onChange={() => setPicked('')}
                                    inputProps={{ 'aria-label': 'None of these Dentally patients' }}
                                />
                                <Typography variant='subtitle2'>None of these</Typography>
                            </Stack>
                        </Box>
                    </Stack>
                </Card>
            );
        }
        if (isUnavailable) {
            return (
                <Alert severity='info' icon={<Iconify icon='mdi:tooth-outline' width={20} />} data-testid='identity-dentally-unavailable'>
                    Couldn&apos;t reach Dentally — we&apos;ll check again after saving.
                </Alert>
            );
        }
        return null;
    };

    return (
        <Card sx={{ p: compact ? 0 : 3, boxShadow: 'none' }} data-testid='identity-check-step'>
            <Typography variant={compact ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700, mb: 0.5 }}>
                Before you continue
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                {hasCrm
                    ? 'This patient may already be in your CRM. Choose how to proceed so the original source is not lost.'
                    : 'We found a matching patient in Dentally.'}
            </Typography>
            <Stack spacing={1.5}>
                {shownCards.map(renderCrmCard)}
                {hiddenCount > 0 && (
                    <Typography variant='caption' color='text.secondary'>
                        +{hiddenCount} more matching {hiddenCount === 1 ? 'lead' : 'leads'} not shown
                    </Typography>
                )}
                {renderDentally()}
                {error && <Alert severity='error'>{error}</Alert>}
            </Stack>
            <Stack direction='row' spacing={1} justifyContent='flex-end' sx={{ mt: 2 }}>
                <Button
                    variant={hasCrm ? 'outlined' : 'contained'}
                    color={hasCrm ? 'inherit' : 'primary'}
                    disabled={busyId != null || askUnanswered}
                    onClick={submit}
                >
                    {hasCrm ? 'Create new anyway' : 'Continue'}
                </Button>
            </Stack>
            {askUnanswered && (
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                    Pick a Dentally patient or &quot;None of these&quot; to continue.
                </Typography>
            )}
        </Card>
    );
}

IdentityCheckStep.propTypes = {
    check: PropTypes.shape({
        crm: PropTypes.shape({ active: PropTypes.array, archived: PropTypes.array }),
        dentally: PropTypes.shape({ status: PropTypes.string, candidates: PropTypes.array }),
    }).isRequired,
    onCreateNew: PropTypes.func.isRequired,
    onOpenLead: PropTypes.func,
    onFollowUp: PropTypes.func,
    onRestore: PropTypes.func,
    compact: PropTypes.bool,
};

IdentityCheckStep.defaultProps = {
    onOpenLead: undefined,
    onFollowUp: undefined,
    onRestore: undefined,
    compact: false,
};
